import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getApplication, getConsent, giveConsent, requestGuardian } from '../api/endpoints'
import { ErrorNotice } from '../components'
import { routeFor } from '../lib/routes'

export default function ConsentPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [agreed, setAgreed] = useState(false)
  const consent = useQuery({ queryKey: ['consent'], queryFn: getConsent })
  const waiting = consent.data?.requiredFrom === 'GUARDIAN' && consent.data.guardianRequested

  // While a guardian is deciding, check the application every 10 seconds so the candidate moves on by themselves.
  const application = useQuery({ queryKey: ['application'], queryFn: getApplication, refetchInterval: waiting ? 10_000 : false })
  useEffect(() => {
    if (application.data && !['REGISTERED', 'CONSENT_PENDING'].includes(application.data.status)) {
      navigate(routeFor(application.data.status), { replace: true })
    }
  }, [application.data, navigate])

  const agree = useMutation({
    mutationFn: () => giveConsent(consent.data!.text.version),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['application'] })
    },
  })
  const ask = useMutation({
    mutationFn: requestGuardian,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['consent'] })
      await queryClient.invalidateQueries({ queryKey: ['application'] })
    },
  })

  if (consent.isPending) return <p role="status">Loading…</p>
  if (consent.isError) return <ErrorNotice error={consent.error} />
  const c = consent.data

  return (
    <div>
      <h1>Consent</h1>
      {c.requiredFrom === 'CANDIDATE' ? (
        <div className="card">
          <p>Please read this and agree before you start the tests.</p>
          <div className="scroll-text" tabIndex={0} aria-label="Consent text">{c.text.body}</div>
          <label className="check">
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
            <span>I have read this and I agree.</span>
          </label>
          <ErrorNotice error={agree.error} />
          <button className="primary" disabled={!agreed || agree.isPending} onClick={() => agree.mutate()}>
            {agree.isPending ? 'Saving…' : 'I agree'}
          </button>
        </div>
      ) : (
        <div className="card stack">
          <p>You are under 18, so a parent or guardian needs to agree before you can take the tests.</p>
          {waiting ? (
            <>
              <div className="notice info" role="status">
                We emailed a link to <strong>{c.guardianEmailHint}</strong>. This page will move on by itself once they have agreed. The link works for 7 days.
              </div>
              <button disabled={ask.isPending} onClick={() => ask.mutate()}>{ask.isPending ? 'Sending…' : 'Send the link again'}</button>
            </>
          ) : (
            <>
              <p className="muted">They will see the text below and confirm with their name.</p>
              <div className="scroll-text" tabIndex={0} aria-label="Consent text for your parent or guardian">{c.text.body}</div>
              <ErrorNotice error={ask.error} />
              <button className="primary" disabled={ask.isPending} onClick={() => ask.mutate()}>
                {ask.isPending ? 'Sending…' : 'Email my parent or guardian'}
              </button>
            </>
          )}
          <p className="muted">Wrong email or details? <Link to="/profile">Change my details</Link></p>
        </div>
      )}
    </div>
  )
}
